import { useCallback, useEffect, useMemo } from "react";

import { SwipeHandler } from "@/components/SwipeHandler";
import { useBoolean } from "@/hooks/useBoolean";
import { useSwipeHandlerName } from "@/hooks/useSwipeHandlerName";
import { PopupProps } from "@/types/framework7-react";

export function usePopupPageReady(pageName: string) {
  const { value: isPageReady, setTrue: readyOn, setFalse: readyOff } = useBoolean(false);

  const { swiperHandlerClassName, swiperHandlerSelector } = useSwipeHandlerName();

  useEffect(() => {
    console.debug("PopupPageReady", pageName, "isPageReady", isPageReady);
  }, [isPageReady, pageName]);

  const getPageProps = useCallback(
    (externalProps?: Partial<PopupProps>): PopupProps => {
      return {
        ...externalProps,

        onPopupClose: (...args) => {
          console.debug("PopupPageReady", pageName, "onPopupClose");
          readyOff();
          externalProps?.onPopupClose?.(...args);
        },

        onPopupClosed: (...args) => {
          console.debug("PopupPageReady", pageName, "onPopupClosed");
          externalProps?.onPopupClosed?.(...args);
        },

        onPopupOpen: (...args) => {
          console.debug("PopupPageReady", pageName, "onPopupOpen");
          readyOn();
          externalProps?.onPopupOpen?.(...args);
        },

        onPopupOpened: (...args) => {
          console.debug("PopupPageReady", pageName, "onPopupOpened");
          externalProps?.onPopupOpened?.(...args);
        },

        onPopupSwipeClose: (...args) => {
          console.debug("PopupPageReady", pageName, "onPopupSwipeClose");
          externalProps?.onPopupSwipeClose?.(...args);
        },

        onPopupSwipeEnd: (...args) => {
          console.debug("PopupPageReady", pageName, "onPopupSwipeEnd");
          externalProps?.onPopupSwipeEnd?.(...args);
        },

        onPopupSwipeMove: (...args) => {
          console.debug("PopupPageReady", pageName, "onPopupSwipeMove");
          externalProps?.onPopupSwipeMove?.(...args);
        },

        onPopupSwipeStart: (...args) => {
          console.debug("PopupPageReady", pageName, "onPopupSwipeStart");
          externalProps?.onPopupSwipeStart?.(...args);
        },

        swipeHandler: swiperHandlerSelector,

        swipeToClose: true,
      };
    },
    [pageName, readyOff, readyOn, swiperHandlerSelector],
  );

  const swipeHandler = useMemo(() => <SwipeHandler className={swiperHandlerClassName} />, [swiperHandlerClassName]);

  return { getPageProps, isPageReady, swipeHandler };
}
